import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Share, Trash2, Check, Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';
import { readJSON, writeJSON, subscribeKey } from '../../../services/deviceStore';
import HospitalIcon from '../../shared/icons/HospitalIcon';
import { notify } from '../../../utils/notify';

/*
 * Personal notes for one hospital rotation. Lightly formatted text (bold,
 * italic, underline, heading, lists) edited in place and saved as you type, on the
 * device and in the profile when signed in. Stored as { html, text, savedAt }; `text`
 * is the plain version used for previews and sharing.
 */

export const notesStorageKey = (hospitalId, rotationId) => `notes:${hospitalId}:${rotationId || 'default'}`;

// Only these tags survive a load, so stored markup stays simple and safe
const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'H3', 'P', 'DIV', 'BR', 'UL', 'OL', 'LI']);
const sanitize = (html) => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED.has(child.tagName)) {
          // Unwrap: keep the text, drop the tag
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
          return;
        }
        [...child.attributes].forEach((a) => child.removeAttribute(a.name));
        walk(child);
      } else if (child.nodeType !== 3) {
        node.removeChild(child);
      }
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

const escapeHtml = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

// Older notes were plain text; present them as paragraphs
const load = (key) => {
  try {
    const parsed = readJSON(key, null);
    if (!parsed) return { html: '', savedAt: null };
    if (parsed.html !== undefined) return { html: sanitize(parsed.html), savedAt: parsed.savedAt || null };
    const html = (parsed.text || '').split('\n').map((l) => `<div>${escapeHtml(l) || '<br>'}</div>`).join('');
    return { html, savedAt: parsed.savedAt || null };
  } catch {
    return { html: '', savedAt: null };
  }
};

const formatSaved = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? `Saved ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : `Saved ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

// Two text sizes (Heading / Body) plus emphasis and lists. No toggle state is
// shown: Heading and Body are explicit, which is simpler than a toggle.
const TOOL_GROUPS = [
  [
    { id: 'bold', cmd: 'bold', icon: Bold, label: 'Bold' },
    { id: 'italic', cmd: 'italic', icon: Italic, label: 'Italic' },
    { id: 'underline', cmd: 'underline', icon: Underline, label: 'Underline' },
  ],
  [
    // Text sizes read as text, the way iOS styles them: a big H and a small Aa
    { id: 'heading', glyph: 'H', glyphClass: 'text-[17px] font-bold', label: 'Header' },
    { id: 'body', glyph: 'Aa', glyphClass: 'text-[13px] font-semibold', label: 'Body text' },
  ],
  [
    { id: 'ul', cmd: 'insertUnorderedList', icon: List, label: 'Bulleted list' },
    { id: 'ol', cmd: 'insertOrderedList', icon: ListOrdered, label: 'Numbered list' },
  ],
];

// The <h3> wrapping the caret, if any (walks the DOM; execCommand's own
// formatBlock query misses headings that contain lists)
const headingAt = (editor) => {
  let node = document.getSelection()?.anchorNode;
  while (node && node !== editor) {
    if (node.nodeType === 1 && node.tagName === 'H3') return node;
    node = node.parentNode;
  }
  return null;
};

// Turn the heading around the caret back into a plain block, keeping its contents
const unwrapHeading = (editor) => {
  const h = headingAt(editor);
  if (!h) return false;
  const div = document.createElement('div');
  while (h.firstChild) div.appendChild(h.firstChild);
  h.replaceWith(div);
  return true;
};

// What the next typed character will carry, for the quiet toolbar tint
const readActive = (editor) => {
  const sel = document.getSelection();
  if (!sel || !sel.isCollapsed || !editor?.contains(sel.anchorNode)) return {};
  return {
    bold: document.queryCommandState('bold'),
    italic: document.queryCommandState('italic'),
    underline: document.queryCommandState('underline'),
    heading: Boolean(headingAt(editor)),
    ul: document.queryCommandState('insertUnorderedList'),
    ol: document.queryCommandState('insertOrderedList'),
  };
};

const RotationNotes = ({ hospital, rotation, onBack }) => {
  const accent = (hospital.color.match(/from-\[([^\]]+)\]/) || [])[1] || '#334155';
  const key = notesStorageKey(hospital.id, rotation?.id);
  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const [savedAt, setSavedAt] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [active, setActive] = useState({});

  // Tint the buttons for whatever the caret will type next (only when nothing is selected)
  useEffect(() => {
    const update = () => setActive(readActive(editorRef.current));
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, []);

  useEffect(() => {
    const show = () => {
      const { html, savedAt: ts } = load(key);
      if (editorRef.current) editorRef.current.innerHTML = html;
      setSavedAt(ts);
      setIsEmpty(!html.replace(/<br>|<div><\/div>/g, '').trim());
    };
    show();
    // Edited on another device: refresh unless the caret is in here right now
    return subscribeKey(key, () => { if (document.activeElement !== editorRef.current) show(); });
  }, [key]);

  const persist = () => {
    const el = editorRef.current;
    if (!el) return;
    const now = Date.now();
    try {
      writeJSON(key, { html: sanitize(el.innerHTML), text: el.innerText, savedAt: now });
      setSavedAt(now);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1200);
    } catch {
      notify.error("Couldn't save. Your device may be out of storage.");
    }
  };

  const handleInput = () => {
    const el = editorRef.current;
    setIsEmpty(!el.innerText.trim());
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 500);
  };

  // Toolbar: act on the current selection without stealing focus from the editor
  const apply = (tool) => (e) => {
    e.preventDefault();
    const editor = editorRef.current;
    editor?.focus();
    if (tool.id === 'heading') {
      document.execCommand('formatBlock', false, 'H3');
    } else if (tool.id === 'body') {
      if (!unwrapHeading(editor)) document.execCommand('formatBlock', false, 'DIV');
    } else {
      // Lists never live inside a heading
      if (tool.id === 'ul' || tool.id === 'ol') unwrapHeading(editor);
      document.execCommand(tool.cmd, false, null);
    }
    setActive(readActive(editor));
    handleInput();
  };

  // Enter at the end of a heading starts a normal line instead of another heading
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && headingAt(editorRef.current)) {
      e.preventDefault();
      document.execCommand('insertParagraph');
      unwrapHeading(editorRef.current) || document.execCommand('formatBlock', false, 'DIV');
      handleInput();
    }
  };

  const share = async () => {
    const text = editorRef.current?.innerText || '';
    const title = `${hospital.shortName || hospital.name} ${rotation?.label || ''} notes`.trim();
    try {
      if (navigator.share) await navigator.share({ title, text });
      else { await navigator.clipboard.writeText(text); notify.success('Copied to clipboard'); }
    } catch (err) {
      if (err?.name !== 'AbortError') notify.error("Couldn't share the note");
    }
  };

  const clear = () => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    if (editorRef.current) editorRef.current.innerHTML = '';
    handleInput();
    persist();
    editorRef.current?.focus();
  };

  const subtitle = rotation?.label ? `${rotation.label} · My Notes` : 'My Notes';

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className={`bg-gradient-to-r ${hospital.color} text-white p-6 pb-8`}>
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} /><span>Back</span>
        </button>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg text-gray-800">
            <HospitalIcon type={hospital.id} className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{hospital.name}</h1>
            <p className="text-white/80 text-sm mt-1">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-3">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          {/* Formatting toolbar */}
          <div className="flex items-center px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/70">
            {TOOL_GROUPS.map((group, i) => (
              <React.Fragment key={group[0].id}>
                {i > 0 && <div className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1.5" aria-hidden="true" />}
                <div className="flex items-center gap-0.5">
                  {group.map((tool) => (
                    <button
                      key={tool.id}
                      type="button"
                      onMouseDown={apply(tool)}
                      onTouchEnd={apply(tool)}
                      aria-label={tool.label}
                      title={tool.label}
                      style={active[tool.id] ? { color: accent } : undefined}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      {tool.icon ? <tool.icon size={17} /> : <span className={`${tool.glyphClass} leading-none`}>{tool.glyph}</span>}
                    </button>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="relative">
            {isEmpty && (
              <div className="pointer-events-none absolute inset-0 p-4 text-[15px] leading-relaxed text-gray-400 dark:text-gray-500">
                Anything you want to remember: door codes, who to call, where things live, tips from the last person…
              </div>
            )}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onBlur={persist}
              role="textbox"
              aria-multiline="true"
              aria-label="Notes"
              spellCheck
              className="notes-editor min-h-[260px] p-4 text-[15px] leading-relaxed text-gray-900 dark:text-white focus:outline-none"
            />
          </div>

          <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/70 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              {justSaved && <Check size={14} className="text-emerald-600" />}
              {formatSaved(savedAt) || 'Not saved yet'}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={share} disabled={isEmpty} aria-label="Share note" className="p-2 rounded-lg disabled:opacity-40">
                <Share size={16} />
              </button>
              <button onClick={clear} disabled={isEmpty} aria-label="Delete note" className="p-2 rounded-lg disabled:opacity-40">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
        <p className="px-1 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
          Your notes are your own and live only on this device. They aren't backed up, so share a copy to yourself if they matter.
        </p>
      </div>
      <div className="h-8" />
    </div>
  );
};

export default RotationNotes;
